import {
  Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef,
  AfterViewInit, ChangeDetectorRef, HostListener
} from "@angular/core";

import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { alertModalWithoutConfirm } from "../helpers/alert";
import { isURL, extractUrls, DOCUMENT_EXTENSIONS, isValidFileType, getReplyThumbnail, getReplyMediaType, getReplyMessageText, getFileExtLabel, getFileBadgeColor } from "./utils/helpers.utils";
import { OnlineUsersService } from "../../service/online-users.service";

interface FilePreviewData {
  files: File[];
  /** Pass true only for group chats */
  isGroup?: boolean;
  /** Active group members (non-removed, non-left), excluding the sender */
  groupMembers?: Array<{
    userId: string;
    displayName?: string;
    userName?: string;
    name?: string;
    profileImage?: string;
  }>;
  /** The channel ID used for URL versioning */
  channelId?: string;
  replyTo?: any;
}

interface MentionEntry {
  userId: string;
  name: string;
  position: number;
  length: number;
  type?: string;
}

interface FilePreviewResult {
  files: File[];
  message: string;
  mentions: MentionEntry[];
  urlMeta?: {
    title?: string;
    description?: string;
    image?: string;
    icon?: string;
    version?: string;
    thread?: any[];
    fileId?: string;
  } | null;
  replyTo?: any;
}

@Component({
  selector: "app-file-preview-modal",
  standalone: true,
  imports: [MatDialogModule, FormsModule, CommonModule],
  template: `
    <div class="file-preview-modal">
      <h2 class="modal-title">Send {{ data.files.length }} File{{ data.files.length > 1 ? 's' : '' }}</h2>

      <!-- File Previews -->
      <div class="file-previews">
        @for (file of data.files; track file; let i = $index) {
          <div class="file-preview-item">
            <!-- Remove Button -->
            <button class="remove-btn" (click)="removeFile(i)" title="Remove file">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            @if (isImage(file)) {
              <img [src]="getPreviewUrl(file)" [alt]="file.name" class="preview-image" />
            } @else if (isVideo(file)) {
              <video [src]="getPreviewUrl(file)" class="preview-video" muted></video>
            } @else {
              <div class="preview-file" [title]="file.name">
                <svg class="preview-file-icon" width="48" height="52" viewBox="0 0 42 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 0C2.686 0 0 2.686 0 6V40C0 43.314 2.686 46 6 46H36C39.314 46 42 43.314 42 40V12L30 0H6Z" [attr.fill]="getFileBadgeColor(file.name)" />
                  <path d="M30 0V8C30 10.209 31.791 12 34 12H42L30 0Z" fill="rgba(255, 255, 255, 0.3)" />
                  <text x="20" y="31" font-size="15" font-weight="500" fill="#ffffff" text-anchor="middle" letter-spacing="0.5px">
                    {{ getFileExtLabel(file.name) }}
                  </text>
                </svg>
              </div>
            }
          </div>
        }

        <!-- Add More Files Button -->
        <div class="file-preview-item add-more-item" (click)="triggerFileInput()">
          <div class="add-more-content">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="#86A8E7" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="add-more-text">Add More</span>
          </div>
        </div>
      </div>

      <!-- Hidden File Input -->
      <input
        #fileInput
        type="file"
        [multiple]="true"
        (change)="onFileSelected($event)"
        class="hidden-file-input"
        [accept]="allowedAcceptTypes"
      />

      <!-- Reply Context Preview -->
      @if (replyTo) {
        <div class="modal-reply-preview">
          <div class="modal-reply-bar">
            <div class="modal-reply-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4e85e2" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 17 4 12 9 7"></polyline>
                <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
              </svg>
            </div>
            <div class="modal-reply-content">
              <span class="modal-reply-sender">{{ getReplySenderName() }}</span>
              <span class="modal-reply-text">{{ getReplyMessageText() }}</span>
            </div>
            @if (getReplyThumbnail()) {
              <img [src]="getReplyThumbnail()" class="modal-reply-thumb" alt="Reply thumbnail" />
            }
          </div>
          <button type="button" class="modal-reply-close" (click)="removeReply()" title="Cancel reply">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6L6 18M6 6L18 18"/>
            </svg>
          </button>
        </div>
      }

      <!-- Additional Message Input -->
      <div class="message-input-section" style="position:relative;">
        <label for="additional-message">Add a message (optional)</label>
        <textarea
          #captionInput
          id="additional-message"
          [(ngModel)]="additionalMessage"
          class="message-textarea"
          placeholder="Type a message to send with the file..."
          rows="3"
          (keydown)="onCaptionKeydown($event)"
          (input)="onCaptionInput($event)"
          autofocus
          cdkFocusInitial
        ></textarea>

        <!-- Mention Dropdown -->
        @if (data.isGroup && showMentionList && filteredMentionUsers.length > 0) {
          <div class="caption-mention-dropdown" #mentionDropdown>
            @for (user of filteredMentionUsers; track user.userId) {
              <div
                class="mention-item"
                [class.mention-item-active]="mentionActiveIndex === $index"
                (mousedown)="$event.preventDefault(); selectMention(user)"
                (mouseover)="mentionActiveIndex = $index"
              >
                @if (user.userId === '__all__') {
                  <div class="mention-avatar mention-all-avatar">&#64;</div>
                } @else {
                  <img
                    [src]="user.profileImage || 'assets/user-icon.png'"
                    class="mention-avatar"
                    width="30" height="30"
                    (error)="handleAvatarError($event)"
                  />
                }
                <div class="mention-info">
                  <span class="mention-name">{{ user.displayName || user.userName || user.name }}</span>
                  @if (user.userId === '__all__') {
                    <span class="mention-desc">Mention all members in group chat</span>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button class="btn-cancel" (click)="onCancel()">Cancel</button>
        <button class="btn-send" [disabled]="data.files.length === 0 || isSending" (click)="onSend()">
          @if (isSending) { Sending... } @else { Send }
        </button>
      </div>
    </div>
  `,
  styles: `
    .file-preview-modal {
      padding: 24px;
      max-width: 600px;
    }

    .modal-title {
      margin: 0 0 20px 0;
      font-size: 20px;
      font-weight: 600;
      color: #333;
    }

    .file-previews {
      display: flex;
      gap: 12px;
      margin-bottom: 20px;
      flex-wrap: wrap;
      max-height: 300px;
      overflow-y: auto;
    }

    .file-preview-item {
      flex: 0 0 calc(50% - 6px);
      aspect-ratio: 1;
      border-radius: 12px;
      overflow: hidden;
      border: 1px solid #e0e0e0;
      background: #f5f5f5;
      position: relative;
    }

    .preview-image,
    .preview-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .preview-file {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px;
      height: 100%;
      width: 100%;
      box-sizing: border-box;
    }

    .preview-file-icon {
      display: block;
      filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.15));
      transition: transform 0.15s ease;
    }

    .preview-file-icon text {
      font-size: 15px;
      font-weight: 500;
      fill: #ffffff;
      text-anchor: middle;
      letter-spacing: 0.5px;
    }

    .preview-file:hover .preview-file-icon {
      transform: scale(1.05);
    }

    /* Remove Button */
    .remove-btn {
      position: absolute;
      top: 2px;
      right: 2px;
      width: 23px;
      height: 23px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.6);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      z-index: 10;
      padding: 0;
    }

    .remove-btn:hover {
      background: rgba(220, 38, 38, 0.9);
      transform: scale(1.1);
    }

    .remove-btn:active {
      transform: scale(0.95);
    }

    .remove-btn svg {
      display: block;
    }

    /* Add More Item */
    .add-more-item {
      cursor: pointer;
      border: 2px dashed #86A8E7;
      background: rgba(134, 168, 231, 0.05);
      transition: all 0.2s ease;
    }

    .add-more-item:hover {
      background: rgba(134, 168, 231, 0.1);
      border-color: #6565AC;
      transform: translateY(-2px);
    }

    .add-more-item:active {
      transform: translateY(0);
    }

    .add-more-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      gap: 8px;
    }

    .add-more-text {
      font-size: 13px;
      font-weight: 500;
      color: #86A8E7;
    }

    .add-more-item:hover .add-more-text {
      color: #6565AC;
    }

    /* Hidden File Input */
    .hidden-file-input {
      display: none;
    }

    /* Message Input Section */
    .message-input-section {
      margin-bottom: 20px;
    }

    .message-input-section label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: #555;
      margin-bottom: 8px;
    }

    .message-textarea {
      width: 100%;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 14px;
      font-family: inherit;
      resize: vertical;
      box-sizing: border-box;
    }

    .message-textarea:focus {
      outline: none;
      border-color: #86A8E7;
      box-shadow: 0 0 0 3px rgba(134, 168, 231, 0.1);
    }

    /* Caption Mention Dropdown */
    .caption-mention-dropdown {
      position: absolute;
      bottom: calc(100% + 4px);
      left: 0;
      right: 0;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
      max-height: 200px;
      overflow-y: auto;
      z-index: 99999;
    }

    .mention-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 14px;
      cursor: pointer;
      transition: background 0.15s;
    }

    .mention-item:hover,
    .mention-item-active {
      background: #f0f4ff;
    }

    .mention-avatar {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    .mention-all-avatar {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: linear-gradient(135deg, #6565AC, #86A8E7);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 700;
      flex-shrink: 0;
    }

    .mention-info {
      display: flex;
      flex-direction: column;
    }

    .mention-name {
      font-size: 14px;
      font-weight: 500;
      color: #1a202c;
    }

    .mention-desc {
      font-size: 11px;
      color: #718096;
    }

    /* Modal Actions */
    .modal-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    }

    .btn-cancel,
    .btn-send {
      padding: 10px 24px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-cancel {
      background: #f5f5f5;
      color: #666;
    }

    .btn-cancel:hover {
      background: #e8e8e8;
    }

    .btn-send {
      background: linear-gradient(135deg, #6565AC, #86A8E7);
      color: white;
    }

    .btn-send:hover:not(:disabled) {
      opacity: 0.9;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(101, 101, 172, 0.4);
    }

    .btn-send:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Scrollbar Styling */
    .file-previews::-webkit-scrollbar {
      width: 6px;
    }

    .file-previews::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    .file-previews::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 3px;
    }

    .file-previews::-webkit-scrollbar-thumb:hover {
      background: #999;
    }

    /* Dark Theme Overrides */
    :host-context(.dark-theme) .file-preview-modal {
      background: var(--bg-card);
      color: var(--text-primary);
    }

    :host-context(.dark-theme) .modal-title {
      color: var(--text-primary);
    }

    :host-context(.dark-theme) .file-preview-item {
      background: var(--bg-app-solid);
      border-color: var(--border-color);
    }

    :host-context(.dark-theme) .file-name {
      color: var(--text-primary);
    }

    :host-context(.dark-theme) .file-size {
      color: var(--text-muted);
    }

    :host-context(.dark-theme) .message-input-section label {
      color: var(--text-secondary);
    }

    :host-context(.dark-theme) .message-textarea {
      background: var(--bg-input);
      color: var(--text-primary);
      border-color: var(--border-color);
    }

    :host-context(.dark-theme) .message-textarea::placeholder {
      color: var(--placeholder-input);
    }

    :host-context(.dark-theme) .btn-cancel {
      background: var(--bg-hover);
      color: var(--text-primary);
    }

    :host-context(.dark-theme) .btn-cancel:hover {
      background: var(--bg-active);
    }

    :host-context(.dark-theme) .add-more-item {
      background: rgba(134, 168, 231, 0.03);
    }

    :host-context(.dark-theme) .add-more-item:hover {
      background: rgba(134, 168, 231, 0.08);
      border-color: #91eae4;
    }

    :host-context(.dark-theme) .add-more-item:hover .add-more-text {
      color: #91eae4;
    }

    :host-context(.dark-theme) .file-previews::-webkit-scrollbar-track {
      background: var(--bg-input);
    }

    :host-context(.dark-theme) .file-previews::-webkit-scrollbar-thumb {
      background: var(--bg-hover);
    }

    :host-context(.dark-theme) .file-previews::-webkit-scrollbar-thumb:hover {
      background: var(--border-color);
    }

    :host-context(.dark-theme) .caption-mention-dropdown {
      background: var(--bg-card);
      border-color: var(--border-color);
    }

    :host-context(.dark-theme) .mention-item:hover,
    :host-context(.dark-theme) .mention-item-active {
      background: var(--bg-hover);
    }

    :host-context(.dark-theme) .mention-name {
      color: var(--text-primary);
    }

    :host-context(.dark-theme) .mention-desc {
      color: var(--text-muted);
    }

    /* Reply Context in Modal */
    .modal-reply-preview {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 8px 12px;
      margin-bottom: 14px;
      background: rgba(134, 168, 231, 0.08);
      border-radius: 8px;
      border-left: 3px solid #4e85e2;
    }

    .modal-reply-bar {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
      flex: 1;
    }

    .modal-reply-icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }

    .modal-reply-content {
      display: flex;
      flex-direction: column;
      min-width: 0;
      flex: 1;
      overflow: hidden;
    }

    .modal-reply-sender {
      font-size: 12px;
      font-weight: 600;
      color: #4e85e2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .modal-reply-text {
      font-size: 12px;
      color: #555;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .modal-reply-thumb {
      width: 32px;
      height: 32px;
      border-radius: 4px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .modal-reply-close {
      background: transparent;
      border: none;
      cursor: pointer;
      color: #888;
      padding: 4px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      flex-shrink: 0;
    }

    .modal-reply-close:hover {
      background: rgba(0, 0, 0, 0.06);
      color: #333;
    }

    :host-context(.dark-theme) .modal-reply-preview {
      background: rgba(255, 255, 255, 0.06);
      border-left-color: #8ba3e8;
    }

    :host-context(.dark-theme) .modal-reply-sender {
      color: #93c5fd;
    }

    :host-context(.dark-theme) .modal-reply-text {
      color: #ccc;
    }

    :host-context(.dark-theme) .modal-reply-close {
      color: #aaa;
    }

    :host-context(.dark-theme) .modal-reply-close:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
  `
})
export class FilePreviewModalComponent implements OnInit, OnDestroy, AfterViewInit {
  allowedAcceptTypes = `image/*,video/*,audio/*,${DOCUMENT_EXTENSIONS.join(',')}`;
  additionalMessage = "";
  isSending = false;
  replyTo: any = null;

  // Mention state
  showMentionList = false;
  mentionSearchTerm = "";
  currentMentionPosition = 0;
  mentionActiveIndex = 0;
  collectedMentions: MentionEntry[] = [];

  private previewUrls = new Map<File, string>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('captionInput') captionInput!: ElementRef<HTMLTextAreaElement>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FilePreviewData,
    private dialogRef: MatDialogRef<FilePreviewModalComponent>,
    private onlineUsersService: OnlineUsersService,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.replyTo = this.data.replyTo || null;
    this.data.files.forEach(file => {
      if (this.isImage(file) || this.isVideo(file)) {
        this.previewUrls.set(file, URL.createObjectURL(file));
      }
    });
  }

  removeReply(): void {
    this.replyTo = null;
  }

  getReplySenderName(): string {
    return this.replyTo?.senderName || this.replyTo?.name || 'User';
  }

  getReplyMessageText(): string {
    return getReplyMessageText(this.replyTo) || 'Media';
  }

  getReplyThumbnail(): string | null {
    return getReplyThumbnail(this.replyTo);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.captionInput) {
        this.captionInput.nativeElement.focus();
      }
    }, 150);
  }

  ngOnDestroy() {
    this.previewUrls.forEach(url => URL.revokeObjectURL(url));
    this.previewUrls.clear();
  }

  // ─── Mention Logic ────────────────────────────────────────────────────────

  /** All mentionable members including the synthetic @all entry */
  private get allMentionableWithAll(): any[] {
    const members = (this.data.groupMembers || []);
    const allEntry = {
      userId: '__all__',
      displayName: 'All',
      userName: 'all',
      name: 'All',
      profileImage: ''
    };
    return [allEntry, ...members];
  }

  get filteredMentionUsers(): any[] {
    const term = this.mentionSearchTerm.toLowerCase();
    if (!term) return this.allMentionableWithAll;
    return this.allMentionableWithAll.filter(u => {
      if (u.userId === '__all__') {
        return 'all'.includes(term);
      }
      const name = (u.displayName || u.userName || u.name || '').toLowerCase();
      return name.includes(term);
    });
  }

  onCaptionInput(event: Event) {
    if (!this.data.isGroup) return;
    const ta = event.target as HTMLTextAreaElement;
    const text = ta.value;
    const cursorPos = ta.selectionStart ?? text.length;

    const lastAtPos = text.lastIndexOf('@', cursorPos - 1);

    if (lastAtPos >= 0) {
      if (lastAtPos > 0 && /[\w.+-]/.test(text[lastAtPos - 1])) {
        this.showMentionList = false;
        return;
      }
      const mentionText = text.substring(lastAtPos + 1, cursorPos);
      const valid = /^[a-zA-Z0-9_ ]*$/.test(mentionText);

      if (valid) {
        this.currentMentionPosition = lastAtPos;
        this.mentionSearchTerm = mentionText;
        this.mentionActiveIndex = 0;
        this.showMentionList = true;
      } else {
        this.showMentionList = false;
      }
    } else {
      this.showMentionList = false;
    }
  }

  get isMobile(): boolean {
    if (typeof window !== 'undefined') {
      const hasTouch = 'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
      const isNarrow = window.innerWidth <= 768;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
      return (hasTouch && isNarrow) || isMobileUA || isNarrow;
    }
    return false;
  }

  onCaptionKeydown(event: KeyboardEvent) {
    // Handle mention navigation
    if (this.showMentionList) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.mentionActiveIndex = Math.min(
          this.mentionActiveIndex + 1,
          this.filteredMentionUsers.length - 1
        );
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.mentionActiveIndex = Math.max(this.mentionActiveIndex - 1, 0);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const user = this.filteredMentionUsers[this.mentionActiveIndex];
        if (user) this.selectMention(user);
        return;
      }
      if (event.key === 'Escape') {
        this.showMentionList = false;
        return;
      }
    }

    // Send on Enter without shift (and no active mention picker)
    if (event.key === 'Enter' && !event.shiftKey) {
      if (this.isMobile) {
        return;
      }
      event.preventDefault();
      if (this.data.files && this.data.files.length > 0) {
        this.onSend();
      }
    }
  }

  selectMention(user: any) {
    const isAll = user.userId === '__all__';
    const userName = isAll ? 'all' : (user.displayName || user.userName || user.name || '');

    const textBefore = this.additionalMessage.substring(0, this.currentMentionPosition);
    const textAfter = this.additionalMessage.substring(
      this.currentMentionPosition + 1 + this.mentionSearchTerm.length
    );
    this.additionalMessage = `${textBefore}@${userName} ${textAfter}`;

    const mention: MentionEntry = {
      userId: isAll ? 'all' : user.userId,
      name: userName,
      position: this.currentMentionPosition,
      length: userName.length + 1, // includes the '@'
      ...(isAll ? { type: 'all' } : {})
    };
    this.collectedMentions.push(mention);

    this.showMentionList = false;
    this.mentionSearchTerm = '';
    this.mentionActiveIndex = 0;

    // Return focus to textarea and place cursor after inserted mention
    setTimeout(() => {
      const el = this.captionInput?.nativeElement;
      if (el) {
        el.focus();
        const newPos = this.currentMentionPosition + userName.length + 2;
        el.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }

  handleAvatarError(event: any): void {
    if (event?.target) {
      event.target.src = 'assets/user-icon.png';
    }
  }

  getFileExtLabel(name: string): string {
    return getFileExtLabel(name);
  }

  getFileBadgeColor(name: string): string {
    return getFileBadgeColor(name);
  }

  isImage(file: File): boolean {
    const name = (file?.name || '').toLowerCase();
    if (DOCUMENT_EXTENSIONS.some(ext => name.endsWith(ext.toLowerCase()))) return false;
    return file.type.startsWith("image/");
  }

  isVideo(file: File): boolean {
    const name = (file?.name || '').toLowerCase();
    if (DOCUMENT_EXTENSIONS.some(ext => name.endsWith(ext.toLowerCase()))) return false;
    return file.type.startsWith("video/") && /\.(mp4|mov|avi|mkv|webm)$/i.test(name);
  }

  getPreviewUrl(file: File): string {
    return this.previewUrls.get(file) || '';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const remainingLimit = 100 - this.data.files.length;

      if (remainingLimit <= 0) {
        alertModalWithoutConfirm('warning', 'Only 100 files are allowed at one time...');
        input.value = '';
        return;
      }

      const selectedFiles = Array.from(input.files);
      const validFiles = selectedFiles.filter(file => isValidFileType(file));

      if (validFiles.length === 0) {
        alertModalWithoutConfirm('warning', 'Please select valid files');
        input.value = '';
        return;
      }

      if (validFiles.length !== selectedFiles.length) {
        alertModalWithoutConfirm('warning', 'Some files were skipped due to unsupported format');
      }

      let newFiles = validFiles;

      if (newFiles.length > remainingLimit) {
        alertModalWithoutConfirm('warning', 'Only 100 files are allowed at one time...');
        newFiles = newFiles.slice(0, remainingLimit);
      }

      // Create preview URLs for images and videos
      newFiles.forEach(file => {
        if (this.isImage(file) || this.isVideo(file)) {
          this.previewUrls.set(file, URL.createObjectURL(file));
        }
      });

      // Add new files to the existing files array
      this.data.files = [...this.data.files, ...newFiles];

      // Reset the input so the same files can be selected again if needed
      input.value = '';
    }
  }

  removeFile(index: number): void {
    const fileToRemove = this.data.files[index];

    // Revoke the preview URL if it exists
    if (this.previewUrls.has(fileToRemove)) {
      URL.revokeObjectURL(this.previewUrls.get(fileToRemove)!);
      this.previewUrls.delete(fileToRemove);
    }

    // Remove the file from the array
    this.data.files = this.data.files.filter((_, i) => i !== index);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  /** Send: optionally fetch URL version, then close with result */
  async onSend(): Promise<void> {
    if (this.isSending) return;
    this.isSending = true;

    const msg = this.additionalMessage;
    let urlMeta: FilePreviewResult['urlMeta'] = null;

    // Issue 2: URL versioning — if the caption contains any URL(s), fetch version
    if (msg && msg.trim() && this.data.channelId) {
      const urls = extractUrls(msg);
      if (urls.length === 0 && isURL(msg.trim())) {
        urls.push(msg.trim().replace(/\/+$/, ''));
      }
      for (const rawUrl of urls) {
        try {
          const normalizedUrl = rawUrl.replace(/\/+$/, '');
          const body: any = { url: normalizedUrl, channelId: this.data.channelId };
          const res: any = await (this.onlineUsersService as any).isUrlValidSave(body);
          if (res?.data) {
            urlMeta = {
              title: res.data.meta?.title || '',
              description: res.data.meta?.description || '',
              image: res.data.meta?.image || '',
              icon: res.data.meta?.icon || '',
              version: res.data.version || '',
              thread: res.data.thread || [],
              fileId: res.data.fileId
            };
          }
        } catch (e) {
          // URL versioning is best-effort
        }
      }
    }

    const result: FilePreviewResult = {
      files: this.data.files,
      message: msg,
      mentions: this.collectedMentions,
      urlMeta,
      replyTo: this.replyTo
    };
    this.isSending = false;
    this.dialogRef.close(result);
  }
}
